import { useState, useEffect, useRef } from "react";
import { Mail, Send, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const MAX_COPIES = 4;
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/**
 * Reusable dialog for sending an Invoice / Quotation / Delivery-Order PDF
 * to the client via the backend's Gmail SMTP relay.
 *
 * Props:
 *  - open / onOpenChange : controlled dialog state
 *  - kind                : 'invoice' | 'quotation' | 'delivery_order'  (display only)
 *  - defaultTo           : prefill recipient (usually client.email)
 *  - defaultClientName   : used for greeting fallback
 *  - sendFn(payload)     : async function that performs the API call
 */
export function EmailDocumentDialog({
  open, onOpenChange, kind = "invoice",
  defaultTo = "", defaultClientName = "",
  sendFn,
  testIdPrefix = "email-doc",
}) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const docLabel = {
    invoice: "Invoice",
    quotation: "Quotation",
    delivery_order: "Delivery Order",
  }[kind] || "Document";

  // Re-sync the recipient if the dialog is opened later with a different
  // client (e.g. user switches docs without unmounting the dialog).
  useEffect(() => {
    if (open) setTo(defaultTo || "");
  }, [open, defaultTo]);

  const splitEmails = (s) => s.split(/[;,]/).map((x) => x.trim()).filter(Boolean);
  const ccList = splitEmails(cc);
  const bccList = splitEmails(bcc);
  const badEmail = (e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const copyError =
    ccList.length > MAX_COPIES ? `CC allows at most ${MAX_COPIES} addresses`
    : bccList.length > MAX_COPIES ? `BCC allows at most ${MAX_COPIES} addresses`
    : [...ccList, ...bccList].find(badEmail) ? `Invalid address: ${[...ccList, ...bccList].find(badEmail)}`
    : "";

  function addFiles(list) {
    const incoming = Array.from(list || []);
    const next = [...files];
    for (const f of incoming) {
      const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      if (!isPdf) { toast.error(`${f.name}: only PDF files can be attached`); continue; }
      if (f.size > MAX_ATTACHMENT_BYTES) { toast.error(`${f.name}: exceeds the 5 MB limit`); continue; }
      if (next.length >= MAX_ATTACHMENTS) { toast.error(`At most ${MAX_ATTACHMENTS} extra attachments`); break; }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    setFiles(next);
  }

  async function submit() {
    if (!to.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    if (copyError) { toast.error(copyError); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("to", to.trim());
      fd.append("cc", ccList.join(","));
      fd.append("bcc", bccList.join(","));
      if (message.trim()) fd.append("message", message.trim());
      files.forEach((f) => fd.append("attachments", f, f.name));
      await sendFn(fd);
      toast.success(`${docLabel} sent to ${to.trim()}`);
      onOpenChange(false);
      setMessage(""); setFiles([]); setBcc("");
    } catch (e) {
      const d = e?.response?.data?.detail || "Failed to send email";
      toast.error(d);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="size-4 text-primary" />
            Email {docLabel} to client
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div>
            <Label className="overline">To</Label>
            <Input
              data-testid={`${testIdPrefix}-to`}
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@company.tl"
            />
            {defaultClientName && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Will greet recipient as <span className="font-medium">{defaultClientName}</span>
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline">CC (up to {MAX_COPIES}, comma-separated)</Label>
              <Input
                data-testid={`${testIdPrefix}-cc`}
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="ops@company.tl, finance@company.tl"
              />
            </div>
            <div>
              <Label className="overline">BCC (up to {MAX_COPIES}, comma-separated)</Label>
              <Input
                data-testid={`${testIdPrefix}-bcc`}
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="archive@kinos.tl"
              />
            </div>
          </div>
          {copyError && <p className="text-xs text-red-600" data-testid={`${testIdPrefix}-copy-error`}>{copyError}</p>}
          <div>
            <Label className="overline">Extra attachments (PDF only, max 5 MB each)</Label>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden"
                   data-testid={`${testIdPrefix}-attachments-input`}
                   onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" data-testid={`${testIdPrefix}-attach-btn`}
                      disabled={files.length >= MAX_ATTACHMENTS} onClick={() => fileRef.current?.click()}>
                <Paperclip className="size-3.5 mr-1.5" /> Attach PDF
              </Button>
              {files.map((f) => (
                <span key={`${f.name}-${f.size}`} data-testid={`${testIdPrefix}-attachment-chip`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-slate-100 border border-border max-w-full">
                  <span className="truncate max-w-[180px]">{f.name}</span>
                  <span className="text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                  <button type="button" aria-label={`Remove ${f.name}`} className="text-red-600 ml-0.5"
                          onClick={() => setFiles(files.filter((x) => x !== f))}>
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Custom message (optional)</Label>
            <Textarea
              data-testid={`${testIdPrefix}-message`}
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi team, attached is the latest version for your review…"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              The PDF will be attached automatically. KINOS branded header is added.
            </p>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            data-testid={`${testIdPrefix}-cancel`}
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            <X className="size-4 mr-1.5" /> Cancel
          </Button>
          <Button
            data-testid={`${testIdPrefix}-submit`}
            onClick={submit}
            disabled={busy || !to.trim() || !!copyError}
            className="bg-primary hover:bg-primary/90"
          >
            <Send className="size-4 mr-1.5" /> {busy ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
